import { AutoComplete, Dropdown, DropdownWidth, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import React, { useContext, useEffect, useState } from 'react'

import useDriverQueries from '../../../../hooks/useDriverQueries'
import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { YourTheir, YouThey } from '../../utils/driverCopyChange'
import { handleDropdownState } from '../../utils/handleDropdownState'
import { handlePrefilledDropdown } from '../../utils/handlePrefilledDropdown'
import { YourEmploymentStyle } from './DriverYourEmploymentStyle'
const { h4: H4, h5: H5 } = typographyRem

export const DriverYourEmployment = () => {
  const quote = useQuote()

  const {
    state: { isMobile, selectedDriverIndex, showModal },
  } = useContext(ProviderContext)
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const { employmentData, industriesData, occupationsData } = useDriverQueries()
  const { updatedDriverDetails, setUpdatedDriverDetails, updatedAdditionalDriver, setUpdatedAdditionalDriver } =
    useDriverPageContext()

  const additionalDrivers = quote?.details?.additional_drivers || []
  const selectedDriver = !selectedDriverIndex ? quote?.details?.proposer : additionalDrivers[selectedDriverIndex - 1]

  const [employment, setEmployment] = useState<string>('')
  const [occupation, setOccuation] = useState<string>('XXX')
  const [business, setBusiness] = useState<string>('999')

  useEffect(() => {
    setEmployment(selectedDriver?.employment || '')
    setOccuation(selectedDriver?.occupation || 'XXX')
    setBusiness(selectedDriver?.business || '999')

    const inEmployment =
      selectedDriver?.employment === 'E' || selectedDriver?.employment === 'S' || selectedDriver?.employment === 'D'

    const occupationBool = (inEmployment && !!selectedDriver?.occupation) || !inEmployment
    const businessBool = (inEmployment && !!selectedDriver?.business) || !inEmployment
    updateValidInputs({ field: 'employment', value: !!selectedDriver?.employment, index: selectedDriverIndex })
    updateValidInputs({
      field: 'occupation',
      value: occupationBool,
      index: selectedDriverIndex,
    })
    updateValidInputs({
      field: 'business',
      value: businessBool,
      index: selectedDriverIndex,
    })
  }, [selectedDriverIndex])

  const employmentError = showErrorBanner && !validInputs[selectedDriverIndex]?.employment ? notAnsweredMessage : ''
  const occupationError = showErrorBanner && !validInputs[selectedDriverIndex]?.occupation ? notAnsweredMessage : ''
  const businessError = showErrorBanner && !validInputs[selectedDriverIndex]?.business ? notAnsweredMessage : ''

  const handleEmploymentStatus = (value: string) => {
    setEmployment(value)
    DataLayer.customEvent({
      eventName: 'employment',
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    let employmentUpdate
    if (value === 'E' || value === 'S' || value === 'D') {
      employmentUpdate = { employment: value }
    } else {
      setOccuation('XXX')
      setBusiness('999')
      employmentUpdate = { employment: value, occupation: 'XXX', business: '999' }
    }
    !selectedDriverIndex
      ? setUpdatedDriverDetails({ ...updatedDriverDetails, ...employmentUpdate })
      : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, ...employmentUpdate })
  }

  const handleSetBusinessAutoComplete = (value: string) => {
    setBusiness(value)
    DataLayer.customEvent({
      eventName: 'business',
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    !selectedDriverIndex
      ? setUpdatedDriverDetails({ ...updatedDriverDetails, business: value })
      : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, business: value })
  }

  const handleSetOccupationAutoComplete = (value: string) => {
    setOccuation(value)
    DataLayer.customEvent({
      eventName: 'occupation',
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    !selectedDriverIndex
      ? setUpdatedDriverDetails({ ...updatedDriverDetails, occupation: value })
      : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, occupation: value })
  }

  useEffect(() => {
    const inEmployment = employment === 'E' || employment === 'S' || employment === 'D'
    updateValidInputs({ field: 'employment', value: !!employment, index: selectedDriverIndex })
    updateValidInputs({ field: 'occupation', value: !inEmployment, index: selectedDriverIndex })
    updateValidInputs({ field: 'business', value: !inEmployment, index: selectedDriverIndex })
  }, [employment])

  useEffect(() => {
    const inEmployment = employment === 'E' || selectedDriver?.employment === 'S' || selectedDriver?.employment === 'D'
    const isValid = (inEmployment && !!business) || !inEmployment
    updateValidInputs({ field: 'business', value: isValid, index: selectedDriverIndex })
  }, [business])
  useEffect(() => {
    const inEmployment = employment === 'E' || selectedDriver?.employment === 'S' || selectedDriver?.employment === 'D'
    const isValid = (inEmployment && !!occupation) || !inEmployment
    updateValidInputs({ field: 'occupation', value: isValid, index: selectedDriverIndex })
  }, [occupation])

  return (
    <YourEmploymentStyle>
      <GenericSectionStyle>
        <H4 className="title">{`${YourTheir()} employment.`}</H4>
        <div className="generic-wrapper">
          <div className="question-container" id="employment">
            <div className="question-text-container">
              <H5>{`What’s ${YourTheir()} current employment status?`}</H5>
            </div>
            <div className="input-info-container">
              <Dropdown
                handleDropdownChange={option => {
                  handleEmploymentStatus(option.value)
                }}
                isMobile={isMobile}
                options={employmentData?.employment || []}
                preFilled={employmentData && handlePrefilledDropdown(employmentData?.employment, employment)}
                states={handleDropdownState(employment, employmentError)}
                errorMessage={employmentError}
                width={DropdownWidth.Large}
                className="dropdown-large-with-icon dropdown-top"
                placeholder="Select"
                dataCy="employment-status-dropdown-list"
                tabFocusTrap={showModal ? -1 : 0}
                ariaLiveOnFocus
              />
              <ToolTip
                text={`We use information about ${YourTheir()} work, studies or other activities to help work out the cost of your premium by comparing you to drivers in similar circumstances.`}
                className="tool-tip tip-top-small"
                tabFocusTrap={showModal ? -1 : 0}
              />
            </div>
          </div>
          {(employment === 'E' || employment === 'S' || employment === 'D') && (
            <>
              <div className="question-container" id="business">
                <div className="question-text-container">
                  <H5>{`What industry do ${YouThey()} work in?`}</H5>
                </div>
                <div className="autocomplete-info-container">
                  <div className="auto-complete-container autocomplete-above">
                    <AutoComplete
                      options={industriesData?.industries || []}
                      error={businessError}
                      loading={false}
                      onChange={option => handleSetBusinessAutoComplete(option?.value || '')}
                      placeholder="Business category"
                      isValid={!(!business || business === '999')}
                      preSelected={
                        business && business !== '999'
                          ? industriesData?.industries.find(item => item.value === business)
                          : undefined
                      }
                      dataCy="business-category-dropdown-list"
                      tabFocusTrap={showModal ? -1 : 0}
                      fullScreen={false}
                      isOpen={false}
                      ariaLiveOnFocus
                    />
                  </div>
                  <ToolTip
                    text={`Just start typing and choose the category that most closely fits the industry ${YouThey()} work in. To give you an example, a nurse would be looking for 'healthcare'.`}
                    className="tool-tip tip-top-autocomplete"
                    tabFocusTrap={showModal ? -1 : 0}
                  />
                </div>
              </div>
              <div className="question-container" id="occupation">
                <div className="question-text-container">
                  <H5>{`What’s ${YourTheir()} job?`}</H5>
                </div>
                <div className="autocomplete-info-container">
                  <div className="auto-complete-container autocomplete-middle">
                    <AutoComplete
                      options={occupationsData?.occupations || []}
                      error={occupationError}
                      loading={false}
                      onChange={option => handleSetOccupationAutoComplete(option?.value || '')}
                      placeholder="Occupation"
                      isValid={!(!occupation || occupation === 'XXX')}
                      preSelected={
                        occupation && occupation !== 'XXX'
                          ? occupationsData?.occupations.find(item => item.value === occupation)
                          : undefined
                      }
                      dataCy="occupation-dropdown-list"
                      tabFocusTrap={showModal ? -1 : 0}
                      fullScreen={false}
                      isOpen={false}
                      ariaLiveOnFocus
                    />
                  </div>
                  <ToolTip
                    text={`Just start typing and choose the description that most closely fits ${YourTheir()} main job. If you can't find ${YourTheir()} job exactly, go with the closest matching description.`}
                    className="tool-tip tip-top-autocomplete"
                    tabFocusTrap={showModal ? -1 : 0}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </GenericSectionStyle>
    </YourEmploymentStyle>
  )
}
